/**
 * Deklaracje ZUS / PIT / PPK (spec 01 §9) — realny proces miesięczny.
 *
 * Ekran istniał od Etapu 6 jako plan bez wykonania: sześć kroków bez ani jednego
 * ukończonego i lista plików „do wygenerowania". Od Etapu 10 stan każdego kroku
 * liczy się z faktów — zamknięta lista płac, wygenerowane dokumenty, oznaczone
 * wysyłki — a pliki naprawdę powstają: czytelny CSV do sprawdzenia i KEDU 5.6
 * do importu w Płatniku.
 *
 * Generowanie, pobieranie plików (numery PESEL) i oznaczanie wysyłki wymaga
 * uprawnienia „Automat : Deklaracje"; dział HR widzi ten sam stan bez akcji [D28].
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bank,
  Button,
  Card,
  EmptyState,
  InfoBanner,
  Modal,
  ProcessTimeline,
  Select,
  Spinner,
  StatusPill,
  cx,
  useToast,
  type StatusPillTone,
} from '@k1p4/ui'
import { formatDate, formatPLN } from '@k1p4/i18n'
import {
  DECLARATION_STEPS,
  taxYearClosed,
  warsawDay,
  type DeclarationFileView,
  type DeclarationIssue,
} from '@k1p4/shared'
import { api, ApiError, apiToday, openFile } from '../../api/client'
import { useHasPermission } from '../../auth/AuthProvider'
import { ZusRegistrations } from './ZusRegistrations'
import styles from './Declarations.module.css'

const STATUS_TONE: Record<string, StatusPillTone> = {
  todo: 'neutral',
  gen: 'pink',
  sent: 'success',
  err: 'danger',
  /* „Nie dotyczy": komplet korygujący bez raportu imiennego (Etap 13). */
  na: 'neutral',
}

/** „03/2026" — okres tak, jak podpisuje go formularz. */
function periodLabel(month: string): string {
  return formatDate(`${month}-01`, 'period')
}

/** Rok w Polsce — nie w UTC: sylwester po 23.00 to już nowy rok w UTC, a nie u nas. */
const CURRENT_YEAR = Number(warsawDay(apiToday()).slice(0, 4))

/**
 * Lata do wyboru w sekcji rocznej: bieżący i cztery wstecz. Deklarację składa się
 * za rok zamknięty, a korektę można złożyć jeszcze przez pięć lat. Bieżący rok
 * zostaje na liście, ale wystawienie za niego wymaga potwierdzenia (#70).
 */
const PIT4R_YEARS = Array.from({ length: 5 }, (_, index) => CURRENT_YEAR - index)

/** Dokumenty roczne, które przed końcem roku wystawia się tylko z potwierdzeniem (#70). */
type OpenYearKind = 'pit4r' | 'pit8ar' | 'pit11' | 'ift1r' | 'imir'

function issueText(
  t: ReturnType<typeof useTranslation>['t'],
  issue: DeclarationIssue,
): string {
  const { amountGr, deadline, lastClosedMonth, ...rest } = issue.params
  return t(`hrDeclarations.issues.${issue.kind}`, {
    ...rest,
    /* Kwota i termin idą z API jako grosze i data — ekran mówi złotymi i dniem. */
    ...(typeof amountGr === 'number' ? { amount: formatPLN(amountGr) } : {}),
    ...(typeof deadline === 'string' ? { date: formatDate(deadline, 'dot') } : {}),
    ...(typeof lastClosedMonth === 'string' && lastClosedMonth !== ''
      ? { lastClosedMonth: periodLabel(lastClosedMonth) }
      : {}),
  })
}

/**
 * Zastrzeżenia nieblokujące, które dotyczą KONKRETNEGO pliku i stoją w jego
 * wierszu. Pozostałe (brak kodu terminu, parametry niepotwierdzone) mówią
 * o całym miesiącu i nie powtarzają się przy każdym dokumencie.
 */
const ROW_NOTES: ReadonlySet<DeclarationIssue['kind']> = new Set([
  'podatek_zryczaltowany_osobno',
  'korekta_osoba_bez_raportu',
  'rpa_za_duzo_lat',
])

export function Declarations() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canRun = useHasPermission('Automat : Deklaracje')

  const [month, setMonth] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /*
   * Braki blokujące plik XML deklaracji rocznej (Etap 28). Trzymamy je osobno
   * od `error`, bo to nie awaria, a lista rzeczy do uzupełnienia w konfiguracji.
   */
  const [xmlIssues, setXmlIssues] = useState<string[]>([])

  const view = useQuery({
    queryKey: ['declarations', month],
    queryFn: () => api.declarations(month ?? undefined),
  })

  const generate = useMutation({
    mutationFn: (target: string) => api.generateDeclarations(target),
    onSuccess: (result) => {
      /*
       * Korekta to NOWY komplet z kolejnym numerem identyfikatora — mówimy ten
       * numer wprost, bo to on trafia do Płatnika (poradnik ZUS, rozdział III).
       */
      toast.show(
        result.correction
          ? t('hrDeclarations.generatedCorrection', {
              count: result.generated.length,
              number: String(result.setNumber).padStart(2, '0'),
            })
          : t('hrDeclarations.generated', { count: result.generated.length }),
        { tone: 'success' },
      )
      void queryClient.invalidateQueries({ queryKey: ['declarations'] })
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError && cause.code === 'PARAMETERS_MISSING'
          ? 'hrDeclarations.errorParameters'
          : 'hrDeclarations.errorGeneric',
      )
    },
  })

  /*
   * Informacje dla ludzi, nie dla urzędu (Etap 18): ZUS IMIR pokazuje
   * ubezpieczonemu, co za niego odprowadzono, a PIT-11 podatnikowi, ile zarobił
   * i ile zaliczki pobrano. Obie liczą się z zamkniętych list płac.
   */
  const issueImir = useMutation({
    mutationFn: (period: string) => api.issueImir(period),
    onSuccess: (result) => {
      toast.show(
        result.skipped.length === 0
          ? t('hrDeclarations.statements.issued', { count: result.issued })
          : t('hrDeclarations.statements.imirSkipped', {
              issued: result.issued,
              count: result.skipped.length,
            }),
        { tone: result.skipped.length === 0 ? 'success' : 'info' },
      )
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (cause) => {
      setError(
        cause instanceof ApiError && cause.code === 'PAYROLL_NOT_CLOSED'
          ? 'hrDeclarations.statements.errorNoPayroll'
          : 'hrDeclarations.errorGeneric',
      )
    },
  })

  /*
   * Rok, który jeszcze trwa (#70): API odpowiada 409 YEAR_NOT_CLOSED, a ekran
   * otwiera okno z podstawą prawną. Potwierdzenie ponawia to samo żądanie.
   */
  const [openYear, setOpenYear] = useState<{ kind: OpenYearKind; year: number } | null>(null)
  const askOpenYear = (kind: OpenYearKind, year: number, cause: unknown): boolean => {
    if (cause instanceof ApiError && cause.code === 'YEAR_NOT_CLOSED') {
      setOpenYear({ kind, year })
      return true
    }
    return false
  }

  /* Roczna ZUS IMIR (art. 41 ust. 8 u.s.u.s.) — z tą samą bramką roku, co PIT-11 (#70). */
  const issueImirYear = useMutation({
    mutationFn: (input: { year: number; acknowledge: boolean }) =>
      api.issueImir(String(input.year), input.acknowledge),
    onSuccess: (result) => {
      toast.show(
        result.skipped.length === 0
          ? t('hrDeclarations.statements.issued', { count: result.issued })
          : t('hrDeclarations.statements.imirSkipped', {
              issued: result.issued,
              count: result.skipped.length,
            }),
        { tone: result.skipped.length === 0 ? 'success' : 'info' },
      )
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (cause, input) => {
      if (askOpenYear('imir', input.year, cause)) return
      setError(
        cause instanceof ApiError && cause.code === 'PAYROLL_NOT_CLOSED'
          ? 'hrDeclarations.statements.errorNoPayroll'
          : 'hrDeclarations.errorGeneric',
      )
    },
  })

  const issuePit11 = useMutation({
    mutationFn: (input: { year: number; acknowledge: boolean }) =>
      api.issuePit11(String(input.year), input.acknowledge),
    onSuccess: (result) => {
      toast.show(t('hrDeclarations.statements.issued', { count: result.issued }), {
        tone: 'success',
      })
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (cause, input) => {
      if (askOpenYear('pit11', input.year, cause)) return
      setError(
        cause instanceof ApiError && cause.code === 'PAYROLL_NOT_CLOSED'
          ? 'hrDeclarations.statements.errorNoPayroll'
          : 'hrDeclarations.errorGeneric',
      )
    },
  })

  /*
   * IFT-1R (Etap 48): informacja dla nierezydenta o przychodzie z art. 29
   * i podatku pobranym u źródła. Powstaje z tych samych zamkniętych list płac
   * co PIT-11 — ale dla osób, których przychód do PIT-11 nie wchodzi.
   */
  const issueIft1r = useMutation({
    mutationFn: (input: { year: number; acknowledge: boolean }) =>
      api.issueIft1r(String(input.year), input.acknowledge),
    onSuccess: (result) => {
      toast.show(
        result.skipped.length === 0
          ? t('hrDeclarations.statements.issued', { count: result.issued })
          : t('hrDeclarations.statements.ift1rSkipped', {
              count: result.issued,
              skipped: result.skipped.length,
            }),
        { tone: result.skipped.length === 0 ? 'success' : 'info' },
      )
      void queryClient.invalidateQueries({ queryKey: ['documents'] })
    },
    onError: (cause, input) => {
      if (askOpenYear('ift1r', input.year, cause)) return
      setError(
        cause instanceof ApiError && cause.code === 'PAYROLL_NOT_CLOSED'
          ? 'hrDeclarations.statements.errorNoPayroll'
          : 'hrDeclarations.errorGeneric',
      )
    },
  })

  /*
   * PIT-4R (Etap 22): rok podatkowy po stronie FIRMY. Domyślnie rok poprzedni,
   * bo deklarację składa się do końca stycznia — w styczniu interesuje nas rok,
   * który się właśnie zamknął.
   */
  const [pit4rYear, setPit4rYear] = useState(CURRENT_YEAR - 1)
  const pit4r = useQuery({
    queryKey: ['declarations', 'pit4r', pit4rYear],
    queryFn: () => api.pit4r(pit4rYear),
    enabled: canRun,
  })
  const downloadXml = useMutation({
    mutationFn: (id: string) => api.downloadDeclarationXml(id),
    onSuccess: () => {
      toast.show(t('hrDeclarations.xmlDownloaded'), { tone: 'success' })
      setXmlIssues([])
    },
    onError: (caught) => {
      const issues = caught instanceof ApiError ? caught.issues : []
      setXmlIssues(issues.length > 0 ? [...issues] : ['generic'])
    },
  })

  const issuePit4r = useMutation({
    mutationFn: (acknowledge: boolean) => api.issuePit4r(pit4rYear, acknowledge),
    onSuccess: () => {
      toast.show(t('hrDeclarations.pit4r.issued', { year: pit4rYear }), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['declarations'] })
    },
    onError: (cause) => {
      if (askOpenYear('pit4r', pit4rYear, cause)) return
      setError(
        cause instanceof ApiError && cause.code === 'NO_CLOSED_PAYROLL'
          ? 'hrDeclarations.pit4r.errorNoPayroll'
          : 'hrDeclarations.errorGeneric',
      )
    },
  })

  /*
   * Sekcja roczna renderuje się tylko z KOMPLETNĄ odpowiedzią. Bez tego jeden
   * nieoczekiwany kształt payloadu wywala cały ekran deklaracji — a miesięczny
   * komplet nie ma z rocznikiem nic wspólnego.
   */
  const pit4rView = pit4r.data?.summary === undefined ? null : pit4r.data

  /*
   * PIT-8AR (Etap 24): ta sama sekcja roczna, inny formularz. Pokazujemy go
   * tylko wtedy, gdy w roku faktycznie pobrano ryczałt albo deklaracja już
   * istnieje — pusty formularz udawałby rozliczenie, którego nie było [D19].
   */
  const pit8ar = useQuery({
    queryKey: ['declarations', 'pit8ar', pit4rYear],
    queryFn: () => api.pit8ar(pit4rYear),
    enabled: canRun,
  })
  /*
   * Sekcja pokazuje się też wtedy, gdy w roku pobrano wyłącznie podatek
   * u źródła od nierezydentów (Etap 48): PIT-8AR go NIE obejmuje, a milczenie
   * w tym miejscu wyglądałoby jak brak podatku do zadeklarowania (ADR-0049).
   */
  const pit8arView =
    pit8ar.data?.summary === undefined
      ? null
      : pit8ar.data.summary.totalGr > 0 ||
          pit8ar.data.withholdingTaxGr > 0 ||
          pit8ar.data.document !== null
        ? pit8ar.data
        : null
  const issuePit8ar = useMutation({
    mutationFn: (acknowledge: boolean) => api.issuePit8ar(pit4rYear, acknowledge),
    onSuccess: () => {
      toast.show(t('hrDeclarations.pit8ar.issued', { year: pit4rYear }), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['declarations'] })
    },
    onError: (cause) => {
      if (askOpenYear('pit8ar', pit4rYear, cause)) return
      setError('hrDeclarations.errorGeneric')
    },
  })

  /** Potwierdzenie z okna: to samo żądanie, tym razem świadomie za rok, który trwa. */
  const confirmOpenYear = (): void => {
    if (openYear === null) return
    const { kind, year } = openYear
    setOpenYear(null)
    if (kind === 'pit4r') issuePit4r.mutate(true)
    else if (kind === 'pit8ar') issuePit8ar.mutate(true)
    else if (kind === 'pit11') issuePit11.mutate({ year, acknowledge: true })
    else if (kind === 'imir') issueImirYear.mutate({ year, acknowledge: true })
    else issueIft1r.mutate({ year, acknowledge: true })
  }

  /* Plik KEDU przez fetch: odmowa ma zdanie na ekranie, nie surowy JSON (przegląd Fali 2). */
  const downloadKedu = useMutation({
    mutationFn: (month: string) => api.downloadKedu(month),
    onError: (cause) =>
      setError(
        cause instanceof ApiError && cause.code === 'KEDU_INCOMPLETE'
          ? 'hrDeclarations.keduIncomplete'
          : cause instanceof ApiError && cause.code === 'DECLARATION_NOT_READY'
            ? 'hrDeclarations.keduNotReady'
            : cause instanceof ApiError && cause.code === 'PARAMETERS_MISSING'
              ? 'hrDeclarations.errorParameters'
              : 'hrDeclarations.errorGeneric',
      ),
  })

  const markSent = useMutation({
    mutationFn: (id: string) => api.markDeclarationSent(id),
    onSuccess: () => {
      toast.show(t('hrDeclarations.markedSent'), { tone: 'success' })
      void queryClient.invalidateQueries({ queryKey: ['declarations'] })
    },
    onError: () => setError('hrDeclarations.errorGeneric'),
  })

  if (view.isPending) return <Spinner label={t('common.loading')} />
  if (view.isError) return <EmptyState title={t('common.error')} description={t('common.retry')} />

  const data = view.data
  const doneCount = data.steps.filter((step) => step.state === 'done').length
  const currentIndex = data.steps.findIndex((step) => step.state === 'current')
  const blockingIssues = data.issues.filter((issue) => issue.blocking)
  /*
   * Zastrzeżenia nieblokujące CAŁEGO miesiąca (brak kodu terminu, parametry
   * niepotwierdzone, brak danych płatnika) — ekran ich dotąd nie pokazywał.
   * Te, które stoją w wierszu pliku (ROW_NOTES), nie powtarzają się tutaj.
   */
  const monthNotes = data.issues.filter((issue) => !issue.blocking && !ROW_NOTES.has(issue.kind))

  return (
    <>
      <div className={styles.header}>
        <div>
          <p className={styles.lead}>{t('hrDeclarations.lead')}</p>
          {/*
            Miesiąc rozliczeniowy to miesiąc WYPŁATY (ADR-0013), więc mówimy
            wprost, które listy płac do niego weszły — inaczej księgowa szuka
            grudnia pod grudniem, a on rozlicza się w styczniu.
          */}
          <p className={styles.workedMonths}>
            {data.workedMonths.length === 0
              ? t('hrDeclarations.workedMonthsEmpty')
              : t('hrDeclarations.workedMonths', {
                  months: data.workedMonths.map((entry) => periodLabel(entry)).join(', '),
                })}
          </p>
        </div>
        <div className={styles.headerActions}>
          {data.availableMonths.length > 0 ? (
            <Select
              aria-label={t('hrDeclarations.monthLabel')}
              value={data.month}
              onChange={(event) => setMonth(event.target.value)}
            >
              {data.availableMonths.map((entry) => (
                <option key={entry} value={entry}>
                  {periodLabel(entry)}
                </option>
              ))}
            </Select>
          ) : null}
          {canRun && data.payrollClosed ? (
            <Button
              onClick={() => {
                setError(null)
                generate.mutate(data.month)
              }}
              disabled={generate.isPending}
            >
              {data.nextSetNumber > 1
                ? t('hrDeclarations.generateCorrection', {
                    number: String(data.nextSetNumber).padStart(2, '0'),
                  })
                : t('hrDeclarations.generate')}
            </Button>
          ) : null}
        </div>
      </div>

      {error !== null ? (
        <InfoBanner tone="danger" className={styles.banner}>
          {t(error)}
        </InfoBanner>
      ) : null}

      {xmlIssues.length > 0 ? (
        <InfoBanner tone="warning" className={styles.banner}>
          <strong>{t('hrDeclarations.xmlErrorTitle')}</strong>{' '}
          {xmlIssues
            .map((issue) =>
              t(`hrDeclarations.xmlIssues.${issue}`, {
                defaultValue: t('hrDeclarations.xmlErrorGeneric'),
              }),
            )
            .join(' ')}
        </InfoBanner>
      ) : null}

      {data.payrollClosed ? null : (
        <InfoBanner tone="warning" className={styles.banner}>
          {t('hrDeclarations.payrollRequired')}
        </InfoBanner>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHead}>
          <h3 className={styles.sectionTitle}>
            {t('hrDeclarations.processTitle')} · {periodLabel(data.month)}
          </h3>
          <span className={styles.sectionMeta}>
            {t('hrDeclarations.processProgress', { done: doneCount, total: data.steps.length })}
          </span>
        </div>
        <Card pad="md">
          <ProcessTimeline
            tone="onLight"
            current={currentIndex === -1 ? doneCount : currentIndex + 1}
            items={DECLARATION_STEPS.map((step) => ({
              key: step,
              label: t(`hrDeclarations.steps.${step}`),
              owner: t(`hrDeclarations.steps.${step}Desc`),
            }))}
          />
        </Card>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('hrDeclarations.filesTitle')}</h3>
        <Card pad="flush" className={styles.list}>
          {data.files.map((file) => (
            <DeclarationRow
              key={file.kind}
              file={file}
              canRun={canRun}
              onDownload={() => {
                if (file.id !== null) openFile(api.declarationFileUrl(file.id))
              }}
              onKedu={
                file.kind === 'dra' || file.kind === 'rca'
                  ? () => {
                      setError(null)
                      downloadKedu.mutate(data.month)
                    }
                  : undefined
              }
              onMarkSent={() => {
                setError(null)
                if (file.id !== null) markSent.mutate(file.id)
              }}
              busy={markSent.isPending}
            />
          ))}
        </Card>

        {blockingIssues.length > 0 ? (
          <InfoBanner tone="danger" className={styles.issues}>
            <span className={styles.issueList}>
              {blockingIssues.map((issue) => (
                <span key={issue.kind}>{issueText(t, issue)}</span>
              ))}
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate('/app/settings/payroll')}
            >
              {t('hrDeclarations.fixInSettings')}
            </Button>
          </InfoBanner>
        ) : null}

        {monthNotes.length > 0 ? (
          <InfoBanner tone="warning" className={styles.issues}>
            <span className={styles.issueList}>
              {monthNotes.map((issue) => (
                <span key={issue.kind}>{issueText(t, issue)}</span>
              ))}
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void navigate('/app/settings/payroll')}
            >
              {t('hrDeclarations.fixInSettings')}
            </Button>
          </InfoBanner>
        ) : null}
      </section>

      {/*
        Zgłoszenia ubezpieczeniowe (Etap 16) idą innym zegarem niż deklaracje:
        7 dni od zdarzenia, a nie 20. dzień następnego miesiąca. Dlatego osobna
        sekcja z własnymi terminami, w tym samym module ZUS/PIT/PPK.
      */}
      <ZusRegistrations />

      <Modal
        open={openYear !== null}
        title={t('hrDeclarations.openYear.confirmTitle', { year: openYear?.year ?? pit4rYear })}
        onClose={() => setOpenYear(null)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpenYear(null)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={confirmOpenYear}>{t('hrDeclarations.openYear.confirm')}</Button>
          </>
        }
      >
        {openYear !== null ? (
          <p>{t(`hrDeclarations.openYear.confirmBody.${openYear.kind}`, { year: openYear.year })}</p>
        ) : null}
      </Modal>

      {canRun ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('hrDeclarations.statements.title')}</h3>
          <p className={styles.sectionNote}>{t('hrDeclarations.statements.lead')}</p>
          {/*
            Rok podatkowy dla PIT-11, IFT-1R i sekcji rocznych (#70): domyślnie
            poprzedni, bo w styczniu i lutym miesiąc rozliczeniowy należy już do
            nowego roku, a informacje składa się za rok zamknięty.
          */}
          {taxYearClosed(pit4rYear, warsawDay(apiToday())) ? null : (
            <InfoBanner tone="warning" className={styles.banner}>
              {t('hrDeclarations.openYear.banner', { year: pit4rYear })}
            </InfoBanner>
          )}
          <div className={styles.sectionActions}>
            <Select
              aria-label={t('hrDeclarations.statements.taxYear')}
              value={String(pit4rYear)}
              onChange={(event) => setPit4rYear(Number(event.target.value))}
            >
              {PIT4R_YEARS.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </Select>
            <Button
              variant="secondary"
              disabled={issueImir.isPending || !data.payrollClosed}
              onClick={() => {
                setError(null)
                issueImir.mutate(data.month)
              }}
            >
              {t('hrDeclarations.statements.imir', { month: periodLabel(data.month) })}
            </Button>
            <Button
              variant="secondary"
              disabled={issuePit11.isPending}
              onClick={() => {
                setError(null)
                issuePit11.mutate({ year: pit4rYear, acknowledge: false })
              }}
            >
              {t('hrDeclarations.statements.pit11', { year: pit4rYear })}
            </Button>
            <Button
              variant="secondary"
              disabled={issueImirYear.isPending}
              onClick={() => {
                setError(null)
                issueImirYear.mutate({ year: pit4rYear, acknowledge: false })
              }}
            >
              {t('hrDeclarations.statements.imirYear', { year: pit4rYear })}
            </Button>
            {/*
              IFT-1R (Etap 48): dla nierezydentów, których przychód opodatkowano
              u źródła — do PIT-11 nie wchodzi, bo to inny tryb (art. 29 PIT).
            */}
            <Button
              variant="secondary"
              disabled={issueIft1r.isPending}
              onClick={() => {
                setError(null)
                issueIft1r.mutate({ year: pit4rYear, acknowledge: false })
              }}
            >
              {t('hrDeclarations.statements.ift1r', { year: pit4rYear })}
            </Button>
          </div>
        </section>
      ) : null}

      {/*
        Deklaracja ROCZNA płatnika (Etap 22). Stoi osobno od kompletu miesiąca,
        bo ma inny zegar: dwanaście kolumn i termin do końca stycznia.
      */}
      {canRun && pit4rView !== null ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('hrDeclarations.pit4r.title')}</h3>
          <p className={styles.sectionNote}>
            {t('hrDeclarations.pit4r.lead', {
              date: formatDate(pit4rView.summary.deadline, 'dot'),
            })}
          </p>
          <div className={styles.sectionActions}>
            <Select
              aria-label={t('hrDeclarations.pit4r.year')}
              value={String(pit4rYear)}
              onChange={(event) => setPit4rYear(Number(event.target.value))}
            >
              {PIT4R_YEARS.map((year) => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </Select>
            <Button
              variant="secondary"
              disabled={issuePit4r.isPending || pit4rView.closedMonths.length === 0}
              onClick={() => {
                setError(null)
                issuePit4r.mutate(false)
              }}
            >
              {t('hrDeclarations.pit4r.issue', { year: pit4rYear })}
            </Button>
            {pit4rView.document !== null && !pit4rView.document.stale ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    openFile(
                      api.declarationFileUrl(pit4rView.document!.id),
                    )
                  }}
                >
                  {t('hrDeclarations.download')}
                </Button>
                {/*
                  Plik do bramki e-Deklaracje (Etap 28). Sami go nie wysyłamy
                  i nie podpisujemy — jak przy KEDU i PIT-11.
                */}
                <Button
                  variant="secondary"
                  disabled={downloadXml.isPending}
                  title={t('hrDeclarations.xmlHint')}
                  onClick={() => {
                    setXmlIssues([])
                    downloadXml.mutate(pit4rView.document!.id)
                  }}
                >
                  {t('hrDeclarations.downloadXml')}
                </Button>
              </>
            ) : null}
          </div>
          <YearlyDocumentNotes document={pit4rView.document} year={pit4rYear} />
          {pit4rView.closedMonths.length === 0 ? (
            <p className={styles.sectionNote}>{t('hrDeclarations.pit4r.noPayroll')}</p>
          ) : (
            <>
              <p className={styles.sectionNote}>
                {t('hrDeclarations.pit4r.total', {
                  amount: formatPLN(pit4rView.summary.totalGr),
                  count: pit4rView.summary.maxTaxpayers,
                })}
              </p>
              <Card pad="flush" className={styles.list}>
                {pit4rView.summary.months
                  .filter((month) => month.row5Gr > 0)
                  .map((month) => (
                    <div key={month.month} className={styles.historyRow}>
                      <span className={styles.rowTitle}>{periodLabel(month.month)}</span>
                      <span className={styles.rowMeta}>
                        {t('hrDeclarations.pit4r.row1', {
                          count: month.row1TaxpayerCount,
                          amount: formatPLN(month.row1Gr),
                        })}
                        {month.row3Gr > 0
                          ? ` · ${t('hrDeclarations.pit4r.row3', {
                              amount: formatPLN(month.row3Gr),
                            })}`
                          : ''}
                      </span>
                      <span className={styles.rowCost}>{formatPLN(month.row12Gr)}</span>
                    </div>
                  ))}
              </Card>
            </>
          )}
        </section>
      ) : null}

      {canRun && pit8arView !== null ? (
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>{t('hrDeclarations.pit8ar.title')}</h3>
          <p className={styles.sectionNote}>
            {t('hrDeclarations.pit8ar.lead', {
              date: formatDate(pit8arView.summary.deadline, 'dot'),
            })}
          </p>
          <p className={styles.sectionNote}>
            {t('hrDeclarations.pit8ar.total', {
              amount: formatPLN(pit8arView.summary.totalGr),
            })}
          </p>
          {/*
            Podatek u źródła z art. 29 ust. 1 pkt 1 wchodzi od Etapu 52 do
            wiersza 1 formularza (ADR-0053) — pokazujemy jego udział, bo za ten
            sam podatek wychodzi jeszcze imienna informacja IFT-1R.
          */}
          <YearlyDocumentNotes document={pit8arView.document} year={pit4rYear} />
          {pit8arView.withholdingTaxGr > 0 ? (
            <p className={styles.sectionNote}>
              {t('hrDeclarations.pit8ar.withholding', {
                amount: formatPLN(pit8arView.withholdingTaxGr),
              })}
            </p>
          ) : null}
          <div className={styles.sectionActions}>
            <Button
              variant="secondary"
              disabled={issuePit8ar.isPending}
              onClick={() => {
                setError(null)
                issuePit8ar.mutate(false)
              }}
            >
              {t('hrDeclarations.pit8ar.issue', { year: pit4rYear })}
            </Button>
            {pit8arView.document !== null && !pit8arView.document.stale ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    openFile(api.declarationFileUrl(pit8arView.document!.id))
                  }}
                >
                  {t('hrDeclarations.download')}
                </Button>
                <Button
                  variant="secondary"
                  disabled={downloadXml.isPending}
                  title={t('hrDeclarations.xmlHint')}
                  onClick={() => {
                    setXmlIssues([])
                    downloadXml.mutate(pit8arView.document!.id)
                  }}
                >
                  {t('hrDeclarations.downloadXml')}
                </Button>
              </>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>{t('hrDeclarations.historyTitle')}</h3>
        {data.history.length === 0 ? (
          <EmptyState
            icon={<Bank size={28} />}
            title={t('hrDeclarations.historyEmpty')}
            description={t('hrDeclarations.historyEmptyBody')}
          />
        ) : (
          <Card pad="flush" className={styles.list}>
            {data.history.map((entry) => (
              <div key={`${entry.kind}-${entry.month}`} className={styles.historyRow}>
                <span className={styles.rowTitle}>
                  {t(`hrDeclarations.kinds.${entry.kind}`)} · {periodLabel(entry.month)}
                </span>
                <span className={styles.rowMeta}>
                  {entry.sentAt
                    ? t('hrDeclarations.sentAt', {
                        date: formatDate(entry.sentAt, 'dot'),
                      })
                    : null}
                </span>
                <span className={styles.rowCost}>
                  {entry.amountGr === null ? '' : formatPLN(entry.amountGr)}
                </span>
              </div>
            ))}
          </Card>
        )}
      </section>
    </>
  )
}

interface RowProps {
  file: DeclarationFileView
  canRun: boolean
  busy: boolean
  onDownload: () => void
  onKedu?: () => void
  onMarkSent: () => void
}

/** Wiersz listy „Wygenerowane pliki": stan, kwota, termin i realne akcje. */
function DeclarationRow({ file, canRun, busy, onDownload, onKedu, onMarkSent }: RowProps) {
  const { t } = useTranslation()
  const ready = file.status === 'gen' || file.status === 'sent'
  const blocking = file.issues.filter((issue) => issue.blocking)
  const notes = file.issues.filter((issue) => !issue.blocking && ROW_NOTES.has(issue.kind))
  /*
   * Dokument ZUS bez terminu mówi dlaczego — także wtedy, gdy termin zniknął
   * w migracji 0059, a zastrzeżenie jeszcze nie powstało (przegląd Fali 2).
   */
  const unknownCode = file.issues.find((issue) => issue.kind === 'kod_terminu_nieznany')
  const deadlineUnknown =
    (file.kind === 'dra' || file.kind === 'rca' || file.kind === 'rsa' || file.kind === 'rpa') &&
    (file.status === 'gen' || file.status === 'err')

  return (
    <div className={styles.row}>
      <span className={styles.rowIcon} aria-hidden="true">
        <Bank size={18} />
      </span>
      <span className={styles.rowBody}>
        <span className={styles.rowTitle}>
          {t(`hrDeclarations.kinds.${file.kind}`)} · {periodLabel(file.month)}
          {file.setNumber > 1
            ? ` · ${t('hrDeclarations.correction', {
                number: String(file.setNumber).padStart(2, '0'),
              })}`
            : ''}
        </span>
        <span className={styles.rowMeta}>
          {t(`hrDeclarations.kindsDesc.${file.kind}`)}
          {file.peopleCount !== null && file.peopleCount > 0
            ? ` · ${t('hrDeclarations.people', { count: file.peopleCount })}`
            : ''}
        </span>
        {file.deadline !== null ? (
          <span className={styles.rowMeta}>
            {t('hrDeclarations.deadline', { date: formatDate(file.deadline, 'dot') })}
          </span>
        ) : deadlineUnknown ? (
          /* Bez kodu terminu nie wiemy, czy to 5., 15. czy 20. dzień (#10). */
          <span className={styles.rowMeta}>
            {unknownCode
              ? t('hrDeclarations.deadlineUnknownCode', { code: unknownCode.params.code })
              : t('hrDeclarations.deadlineUnknown')}
          </span>
        ) : null}
        {/* Termin zostaje na wierzchu także przy zastrzeżeniu — data nie znika. */}
        {blocking.length > 0 ? (
          <span className={styles.rowIssue}>{issueText(t, blocking[0]!)}</span>
        ) : null}
        {/* Np. druga wpłata podatku przy PIT-4 (#3) — przypomnienie, nie blokada. */}
        {notes.map((issue) => (
          <span key={issue.kind} className={styles.rowNote}>
            {issueText(t, issue)}
          </span>
        ))}
      </span>
      <span className={styles.rowCost}>
        {file.amountGr === null ? '' : formatPLN(file.amountGr)}
      </span>
      <StatusPill tone={STATUS_TONE[file.status] ?? 'neutral'} className={styles.rowStatus}>
        {t(`hrDeclarations.status.${file.status}`)}
      </StatusPill>
      <span className={cx(styles.rowActions, !canRun && styles.rowActionsEmpty)}>
        {canRun && ready ? (
          <Button size="sm" variant="secondary" onClick={onDownload}>
            {t('hrDeclarations.download')}
          </Button>
        ) : null}
        {canRun && ready && onKedu ? (
          <Button size="sm" variant="secondary" onClick={onKedu}>
            {t('hrDeclarations.downloadKedu')}
          </Button>
        ) : null}
        {canRun && file.status === 'gen' ? (
          <Button size="sm" onClick={onMarkSent} disabled={busy}>
            {t('hrDeclarations.markSent')}
          </Button>
        ) : null}
      </span>
    </div>
  )
}

/**
 * Stan zapisanego rocznika (#70): zastrzeżenie z wystawienia w trakcie roku
 * i — po końcu roku — informacja, że dokument trzeba wystawić ponownie.
 */
function YearlyDocumentNotes({
  document,
  year,
}: {
  document: { issues: DeclarationIssue[]; stale: boolean } | null
  year: number
}) {
  const { t } = useTranslation()
  if (document === null) return null
  if (document.stale) {
    return (
      <InfoBanner tone="danger" className={styles.banner}>
        {t('hrDeclarations.openYear.stale', { year })}
      </InfoBanner>
    )
  }
  return (
    <>
      {document.issues.map((issue) => (
        <p key={issue.kind} className={styles.rowNote}>
          {issueText(t, issue)}
        </p>
      ))}
    </>
  )
}
